import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { ArrowLeft, AlertTriangle, Upload, X, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/helpers';

const CancellationRequestCreate = () => {
  const { id: permitId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Form state
  const [reason, setReason] = useState('');
  const [supportingDocuments, setSupportingDocuments] = useState([]);
  const [assignedUser, setAssignedUser] = useState('');

  // Fetch permit details
  const { data: permitData, isLoading: permitLoading } = useQuery(
    ['permit', permitId],
    async () => {
      const response = await api.get(`/permits/${permitId}`);
      return response.data.permit;
    }
  );

  // Fetch available reviewers
  const { data: reviewersData } = useQuery(
    'reviewers',
    async () => {
      const response = await api.get('/users/reviewers');
      return response.data.users;
    }
  );

  // Check for existing pending cancellation request
  const { data: pendingCancellationData } = useQuery(
    ['pendingCancellation', permitId],
    async () => {
      const response = await api.get(`/cancellation-requests/${permitId}/pending`);
      return response.data;
    }
  );

  // Role hierarchy for filtering reviewers
  const ROLE_HIERARCHY = ['bo', 'eac', 'sdo', 'adc', 'dc'];

  // Get available reviewers with higher roles in same subdivision + all DC users
  const availableReviewers = useMemo(() => {
    if (!reviewersData || !user || !permitData) return [];

    const currentRoleIndex = ROLE_HIERARCHY.indexOf(user.role);
    const subdivisionId = permitData.subdivisionId;

    // Filter users with higher roles in same subdivision + all DC users
    return reviewersData.filter(u => {
      const reviewerRoleIndex = ROLE_HIERARCHY.indexOf(u.role);

      // Must be a higher role
      if (reviewerRoleIndex <= currentRoleIndex) return false;

      // DC users always included (district-wide)
      if (u.role === 'dc') return true;

      // Other higher roles must be in same subdivision
      return u.subdivisionId === subdivisionId;
    });
  }, [reviewersData, user, permitData?.subdivisionId]);

  // Group reviewers by role for organized display
  const reviewersByRole = useMemo(() => {
    const grouped = {};
    const roleOrder = ['eac', 'sdo', 'adc', 'dc']; // Order for display

    availableReviewers.forEach(reviewer => {
      const role = reviewer.role;
      if (!grouped[role]) grouped[role] = [];
      grouped[role].push(reviewer);
    });

    // Return as array sorted by role hierarchy
    return roleOrder
      .filter(role => grouped[role] && grouped[role].length > 0)
      .map(role => ({
        level: role,
        label: role.toUpperCase(),
        users: grouped[role]
      }));
  }, [availableReviewers]);

  // Create cancellation request mutation
  const createMutation = useMutation(
    async (data) => {
      const formData = new FormData();
      formData.append('reason', data.reason);
      formData.append('assignedUser', data.assignedUser);

      // Append files directly
      if (data.files && data.files.length > 0) {
        data.files.forEach(file => formData.append('documents', file));
      }

      const response = await api.post(`/cancellation-requests/${permitId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        navigate(`/cancellation-requests/${data.cancellationRequest.id}`);
      }
    }
  );

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setSupportingDocuments(prev => [...prev, ...files]);
    toast.success(`${files.length} file(s) added`);
  };

  const handleRemoveDocument = (index) => {
    setSupportingDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    if (!assignedUser) {
      toast.error('Please select a reviewer');
      return;
    }

    try {
      await createMutation.mutateAsync({
        reason: reason.trim(),
        assignedUser,
        files: supportingDocuments
      });
    } catch (error) {
      console.error('Create cancellation request error:', error);
      toast.error(error.response?.data?.message || error.message);
    }
  };

  // Validation
  const canSubmit = reason.trim() && assignedUser && !createMutation.isLoading;

  // Check if permit is eligible for cancellation
  const isEligible = permitData && ['Active', 'Renewal_Needed'].includes(permitData.status);
  const hasPendingCancellation = pendingCancellationData?.hasPending;

  if (permitLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!permitData) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Permit not found</p>
        </div>
      </div>
    );
  }

  if (!isEligible) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 mt-0.5" />
            <div>
              <h3 className="text-lg font-medium text-yellow-900 mb-2">
                Permit Not Eligible for Cancellation
              </h3>
              <p className="text-yellow-800 mb-4">
                Only permits with status "Active" or "Renewal Needed" can have cancellation requests created.
                This permit has status: <strong>{permitData.status}</strong>
              </p>
              <Link
                to={`/permits/${permitId}`}
                className="btn btn-outline"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Permit
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (hasPendingCancellation) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 mt-0.5" />
            <div>
              <h3 className="text-lg font-medium text-yellow-900 mb-2">
                Pending Cancellation Request Exists
              </h3>
              <p className="text-yellow-800 mb-4">
                This permit already has a pending cancellation request. Only one cancellation request can be active at a time.
              </p>
              <div className="flex gap-3">
                <Link
                  to={`/cancellation-requests/${pendingCancellationData.cancellationRequest.id}`}
                  className="btn btn-primary"
                >
                  View Cancellation Request
                </Link>
                <Link
                  to={`/permits/${permitId}`}
                  className="btn btn-outline"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Permit
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/permits/${permitId}`}
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Permit
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          Request Permit Cancellation
        </h1>
        <p className="text-gray-600 mt-1">
          Submit a cancellation request for permit {permitData.permit_no}
        </p>
      </div>

      {/* Warning Notice */}
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">
              Important: Cancellation is Permanent
            </h3>
            <p className="mt-1 text-sm text-red-700">
              Once a cancellation request is approved, the permit will be permanently cancelled and cannot be reactivated.
              If you need to re-process this permit in the future, you will need to create a new permit application.
            </p>
          </div>
        </div>
      </div>

      {/* Permit Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Permit Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <label className="text-gray-500 text-xs font-medium">Permit Number</label>
            <p className="text-gray-900 font-medium">{permitData.permit_no}</p>
          </div>
          <div>
            <label className="text-gray-500 text-xs font-medium">Current Status</label>
            <p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                permitData.status === 'Active' ? 'bg-green-100 text-green-800' :
                permitData.status === 'Renewal_Needed' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {permitData.status === 'Renewal_Needed' ? 'Renewal Needed' : permitData.status}
              </span>
            </p>
          </div>
          <div>
            <label className="text-gray-500 text-xs font-medium">Permit Holder</label>
            <p className="text-gray-900">{permitData.name}</p>
          </div>
          <div>
            <label className="text-gray-500 text-xs font-medium">Village</label>
            <p className="text-gray-900">{permitData.village}</p>
          </div>
          <div>
            <label className="text-gray-500 text-xs font-medium">Issue Date</label>
            <p className="text-gray-900">{formatDate(permitData.issue_date)}</p>
          </div>
          {permitData.subdivision && (
            <div>
              <label className="text-gray-500 text-xs font-medium">Subdivision</label>
              <p className="text-gray-900">{permitData.subdivision.name}</p>
            </div>
          )}
        </div>
      </div>

      {/* Cancellation Request Form */}
      <form onSubmit={handleSubmit}>
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Cancellation Request</h2>

          {/* Reason */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Cancellation <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Provide a detailed reason for requesting the cancellation of this permit..."
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Please provide a clear and detailed explanation for why this permit should be cancelled.
            </p>
          </div>

          {/* Supporting Documents */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Supporting Documents (Optional)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <div className="text-center">
                <Upload className="mx-auto h-8 w-8 text-gray-400" />
                <div className="mt-2">
                  <label className="cursor-pointer">
                    <span className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                      Upload files
                    </span>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={createMutation.isLoading}
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF, JPG, PNG up to 10MB each
                  </p>
                </div>
              </div>
            </div>

            {/* Uploaded Documents List */}
            {supportingDocuments.length > 0 && (
              <div className="mt-4 space-y-2">
                {supportingDocuments.map((doc, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center flex-1 min-w-0">
                      <FileText className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-900 truncate">{doc.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveDocument(index)}
                      className="ml-2 text-red-600 hover:text-red-700 flex-shrink-0"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

          </div>

          {/* Assign Reviewer */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign to Reviewer <span className="text-red-500">*</span>
            </label>
            <select
              value={assignedUser}
              onChange={(e) => setAssignedUser(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select Reviewer</option>
              {reviewersByRole.map(group => (
                <optgroup key={group.level} label={group.label}>
                  {group.users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.fullName} ({u.designation || u.role.toUpperCase()})
                      {u.subdivisionId === permitData.subdivisionId ? ' - Same Subdivision' : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Select a reviewer with a higher role to review this cancellation request.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Link
            to={`/permits/${permitId}`}
            className="btn btn-outline"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={!canSubmit}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createMutation.isLoading ? 'Submitting...' : 'Submit Cancellation Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CancellationRequestCreate;
